import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { getMyStore, getCardgames, getStoreGames, updateStoreGames } from '../../services/supabaseService';
import { getGameIcon } from '../../constants/games';

export const StoreGames: React.FC = () => {
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [store, setStore] = useState<any>(null);
    const [games, setGames] = useState<any[]>([]);
    const [selectedStoreGames, setSelectedStoreGames] = useState<number[]>([]);

    useEffect(() => {
        if (user && user.role_id === 6) {
            fetchData();
        } else if (user) {
            navigate('/');
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const myStore = await getMyStore();
            if (myStore) {
                setStore(myStore);
                const [gamesData, storeGamesData] = await Promise.all([
                    getCardgames(),
                    getStoreGames(myStore.id)
                ]);
                setGames(gamesData || []);
                setSelectedStoreGames(storeGamesData || []);
            }
        } catch (error) {
            showNotification("Erro ao carregar jogos da loja", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleGame = (gameId: number) => {
        if (selectedStoreGames.includes(gameId)) {
            setSelectedStoreGames(selectedStoreGames.filter(id => id !== gameId));
        } else {
            setSelectedStoreGames([...selectedStoreGames, gameId]);
        }
    };

    const handleSelectAll = () => {
        setSelectedStoreGames(games.map(g => g.id));
    };

    const handleClearAll = () => {
        setSelectedStoreGames([]);
    };

    const handleSaveStoreGames = async () => {
        if (!store) return;
        setSaving(true);
        try {
            const success = await updateStoreGames(store.id, selectedStoreGames);
            if (success) {
                showNotification("Jogos suportados pela loja atualizados com sucesso!", "success");
            } else {
                showNotification("Erro ao atualizar os jogos da loja", "error");
            }
        } catch (error) {
            showNotification("Erro ao salvar jogos da loja", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-12 text-center text-slate-500 font-bold uppercase tracking-widest text-xs animate-pulse">
                Carregando jogos suportados...
            </div>
        );
    }

    if (!store) {
        return (
            <div className="p-12 text-center text-slate-400 font-bold text-sm">
                Loja não encontrada ou permissão insuficiente.
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
                <div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400 mb-1">
                        <Link to="/minha-loja" className="hover:text-purple-400 transition-colors">Configurações</Link>
                        <span>/</span>
                        <span className="text-white">Jogos Suportados</span>
                    </div>
                    <h1 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                        <i className="fas fa-gamepad text-purple-500 text-xl"></i>
                        Jogos Suportados
                    </h1>
                    <p className="text-slate-400 text-xs mt-0.5">
                        Selecione todos os Card Games que sua loja comercializa, possui em estoque ou organiza torneios.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleSelectAll}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-colors"
                    >
                        Selecionar Todos
                    </button>
                    <button
                        type="button"
                        onClick={handleClearAll}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-colors"
                    >
                        Limpar
                    </button>
                    <button
                        onClick={handleSaveStoreGames}
                        disabled={saving}
                        className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black px-5 py-2 rounded-xl text-xs uppercase tracking-wider transition-all shadow-md shadow-purple-600/20 active:scale-95 flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <i className="fas fa-spinner fa-spin text-xs"></i>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check text-xs"></i>
                                Salvar Jogos ({selectedStoreGames.length})
                            </>
                        )}
                    </button>
                </div>
            </header>

            {/* Grid of Games */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-7 space-y-6">
                <div className="flex items-center justify-between pb-3 border-b border-slate-800/80">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                        {selectedStoreGames.length} de {games.length} jogos selecionados
                    </span>
                    <span className="text-[11px] text-slate-400">
                        Clique em cada card game para ativar ou desativar
                    </span>
                </div>

                {games.length === 0 ? (
                    <div className="text-center py-12 bg-slate-950/40 rounded-xl border border-dashed border-slate-800">
                        <i className="fas fa-gamepad text-3xl text-slate-700 mb-3 block"></i>
                        <p className="text-slate-400 font-bold text-sm">Nenhum jogo encontrado no catálogo.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
                        {games.map(game => {
                            const isSelected = selectedStoreGames.includes(game.id);
                            return (
                                <button
                                    key={game.id}
                                    type="button"
                                    onClick={() => handleToggleGame(game.id)}
                                    className={`p-4 rounded-2xl border text-left transition-all duration-200 flex items-center gap-3.5 ${
                                        isSelected 
                                            ? 'bg-purple-500/10 border-purple-500/70 shadow-md shadow-purple-500/5' 
                                            : 'bg-slate-950/50 border-slate-800 hover:border-slate-700 hover:bg-slate-950/80'
                                    }`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-bold uppercase tracking-wider truncate transition-colors ${
                                            isSelected ? 'text-white' : 'text-slate-400'
                                        }`}>
                                            {game.name}
                                        </p>
                                        <p className="text-[10px] text-slate-500 font-mono mt-0.5">{game.slug}</p>
                                    </div>
                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                                        isSelected 
                                            ? 'bg-purple-600 border-purple-500 text-white' 
                                            : 'border-slate-800 bg-slate-950'
                                    }`}>
                                        {isSelected && <i className="fas fa-check text-[9px] font-black"></i>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="pt-4 border-t border-slate-800 flex justify-end">
                    <button
                        onClick={handleSaveStoreGames}
                        disabled={saving}
                        className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black px-8 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-md shadow-purple-600/20 active:scale-95 flex items-center gap-2"
                    >
                        {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                </div>
            </div>
        </div>
    );
};
